/* eslint-disable camelcase */
import * as Yup from 'yup';
import { useCallback, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useRecaptchaToken } from '../../../globalHooks/useRecaptchaToken';
import {
  fetchFormFields,
  fetchDetailsFromAddress,
  findUkAddressesSuggestions,
  postFormData,
} from '../../../../modules/api';
import useParams from '../../../globalHooks/useParams';

const useBrochureForm = () => {
  const params = useParams();

  const [fields, setFields] = useState({});

  const [addressLookup, setAddressLookup] = useState('');

  const [addressSuggestions, setAddressSuggestions] = useState([]);

  const [prev, setPrev] = useState(0);

  const [showLocations, setShowLocations] = useState(false);

  const [waiting, setWaiting] = useState(true);

  const [submitInfo, setSubmitInfo] = useState(null);

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const [initialValues, setInitialValues] = useState({
    brochures: [],
    first_name: '',
    last_name: '',
    phone: '',
    how_hear: null,
    postcode: '',
    start_address: '',
    address_line_1: '',
    address_line_2: '',
    town: '',
    county: '',
    email: '',
    opt_in: false,
    honeypot: null,
    current_page: window.location.href,
    referrer: document.referrer,
    client_id: 'No client id detected',
  });

  const [cookies] = useCookies(['_ga']);
  const executeRecaptcha = useRecaptchaToken();

  // Get _ga client id
  const getGaClientId = () => {
    const gaCookie = cookies._ga;

    if (gaCookie) {
      const parts = gaCookie.split('.');
      if (parts.length === 4) {
        return `${parts[2]}.${parts[3]}`;
      }
    }

    return 'No client id detected';
  };

  // Set client id when _ga cookie is available
  useEffect(() => {
    const gaClientId = getGaClientId();

    setInitialValues((prevValues) => ({
      ...prevValues,
      client_id: gaClientId,
    }));
  }, [cookies._ga]);

  const validationSchema = Yup.object({
    brochures: Yup.array().min(1).required('* required'),
    first_name: Yup.string().required('* required'),
    last_name: Yup.string().required('* required'),
    address_line_1: Yup.string().required('* required'),
    town: Yup.string().required('* required'),
    postcode: Yup.string()
      .matches(
        /^[A-Za-z]{1,2}\d[A-Za-z\d]?( )?\d[A-Za-z]{2}$/,
        'Please enter a valid UK postcode'
      )
      .required('* required'),
    email: Yup.string()
      .email('Please enter a valid email address')
      .required('* required'),
  });

  // Get fields on load
  useEffect(() => {
    (async () => {
      const data = await fetchFormFields('brochure');

      setFields(data);
      setSubmitInfo(data.thank_you);

      setWaiting(false);
    })();
  }, []);

  useEffect(() => {
    if (addressLookup.trim().length > 0) {
      getAddressSuggestions(addressLookup);
    }
  }, [addressLookup]);

  const getAddressSuggestions = async (address) => {
    const delay = 1500;
    const now = new Date().getTime();

    if (now - prev > delay) {
      let data = [];

      try {
        data = await findUkAddressesSuggestions(address);
      } catch (error) {
        console.error("Can't find addresses");
      }

      setAddressSuggestions(data);
      setPrev(now);
    }
  };

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.error('Execute recaptcha not yet available');
      return null;
    }

    const token = await executeRecaptcha('brochure');

    return token;
  }, [executeRecaptcha]);

  const handleBrochureChange = (value, currentValues, setFieldValue) => {
    let outputBrochures;

    if (currentValues.includes(value)) {
      outputBrochures = currentValues.filter((brochure) => brochure !== value);
    } else {
      outputBrochures = [...currentValues, value];
    }

    setFieldValue('brochures', outputBrochures);
  };

  const handleAddressClick = async (address, setFieldValue) => {
    const response = await fetchDetailsFromAddress(address);

    if (response.address) {
      const { postcode, town_or_city, line_1, line_2, county } =
        response.address;

      setFieldValue('postcode', postcode);
      setFieldValue('town', town_or_city);
      setFieldValue('address_line_1', line_1);
      setFieldValue('address_line_2', line_2);
      setFieldValue('county', county);

      setAddressLookup('');
      setAddressSuggestions([]);
    }
  };

  const handlePickLocation = (value, setFieldValue) => {
    setFieldValue('address', value.line_1);
    setFieldValue('county', value.county);
    setFieldValue('country', value.country);
    setFieldValue('town', value.town_or_city);
  };

  const postForm = async (data, recaptchaToken, callback) => {
    if (!recaptchaToken || recaptchaToken.trim().length < 1) {
      alert('Please fill out the Recaptcha');
    } else {
      setWaiting(true);

      try {
        const extraFields = { form_reference: 'brochure', id: 'brochure-form' };
        await postFormData({ ...data, ...extraFields }, recaptchaToken);

        if (callback) callback();

        if (submitInfo.type === 'page') {
          window.location.href = submitInfo.page;
        } else {
          setShowSuccessDialog(true);
        }
      } catch (error) {
        console.error(error);

        if (error.response?.data?.message?.includes('recaptcha')) {
          alert('Invalid recaptcha response');
        }
      }

      setWaiting(false);
    }
  };

  const disable = (errors, values, initialValues) => {
    const hasErrors = Object.keys(errors).length > 0;

    return (
      hasErrors || JSON.stringify(values) === JSON.stringify(initialValues)
    );
  };

  return {
    validationSchema,
    initialValues,
    disable,
    postForm,
    fields,
    showLocations,
    setShowLocations,
    handleBrochureChange,
    handlePickLocation,
    submitInfo,
    showSuccessDialog,
    setShowSuccessDialog,
    handleReCaptchaVerify,
    addressSuggestions,
    handleAddressClick,
    addressLookup,
    setAddressLookup,
    waiting,
  };
};

export default useBrochureForm;
